import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { Box, Typography, Button, CircularProgress, Paper, Alert } from '@mui/material';
import config from '../config';

const AcceptConnectionPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { API_BASE_URL } = config;

  const reduxIsAuthenticated = useSelector((state) => state.auth.isAuthenticated);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [invitationDetails, setInvitationDetails] = useState(null);
  const [outcome, setOutcome] = useState('');
  const [cookieChecked, setCookieChecked] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // ✅ Check auth via your /profile route (cookie-based)
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/v1/profile`, {
          withCredentials: true, // send cookies
        });
        if (res.status === 200 && res.data?.user) {
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(reduxIsAuthenticated || false);
        }
      } catch {
        setIsAuthenticated(reduxIsAuthenticated || false);
      } finally {
        setCookieChecked(true);
      }
    };
    checkAuth();
  }, [API_BASE_URL, reduxIsAuthenticated]);

  // ✅ Validate token after cookie check
  useEffect(() => {
    if (!cookieChecked) return;

    if (!token) {
      setError('No invitation token provided.');
      setLoading(false);
      return;
    }

    const validateInvitation = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`${API_BASE_URL}/api/v1/verify-invite-token/${token}`, {
          withCredentials: true,
        });
        setInvitationDetails(response.data.data);
        setError('');
      } catch (err) {
        setError(err.response?.data?.message || 'This invitation is invalid or has expired.');
        setInvitationDetails(null);
      } finally {
        setLoading(false);
      }
    };

    validateInvitation();
  }, [token, API_BASE_URL, cookieChecked]);

  const handleResponse = async (action) => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: `/accept-connection/${token}` } });
      return;
    }

    setLoading(true);
    setError('');
    const endpoint = action === 'accept' ? `/api/v1/accept-link/${token}` : `/api/v1/reject-link/${token}`;

    try {
      await axios.post(`${API_BASE_URL}${endpoint}`, {}, { withCredentials: true });
      setOutcome(`Invitation successfully ${action}ed!`);
      setTimeout(() => navigate('/connections'), 2000);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        setError('Session expired. Please login again.');
        setTimeout(() => navigate('/login', { state: { from: `/accept-connection/${token}` } }), 2000);
      } else {
        setError(err.response?.data?.message || `Failed to ${action} invitation.`);
      }
    } finally {
      setLoading(false);
    }
  };

  const renderContent = () => {
    if (loading) {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">Loading...</Typography>
        </Box>
      );
    }

    if (error) return <Alert severity="error">{error}</Alert>;
    if (outcome) return <Alert severity="success">{outcome}</Alert>;

    if (invitationDetails) {
      return (
        <>
          <Typography variant="h5" gutterBottom>Connection Request</Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            <strong>{invitationDetails.initiator.username}</strong> has invited you to connect.
          </Typography>
          {invitationDetails.message && (
            <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'grey.100' }}>
              <Typography variant="body2">"{invitationDetails.message}"</Typography>
            </Paper>
          )}

          {!isAuthenticated ? (
            <Box sx={{ my: 2 }}>
              <Alert severity="info" sx={{ mb: 2 }}>Login required to respond.</Alert>
              <Button
                variant="contained"
                onClick={() => navigate('/login', { state: { from: `/accept-connection/${token}` } })}
              >
                Go to Login
              </Button>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
              <Button variant="contained" color="success" onClick={() => handleResponse('accept')}>
                Accept
              </Button>
              <Button variant="outlined" color="error" onClick={() => handleResponse('reject')}>
                Reject
              </Button>
            </Box>
          )}
        </>
      );
    }

    return null;
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
      <Paper elevation={3} sx={{ p: 4, maxWidth: '500px', width: '100%', textAlign: 'center' }}>
        {renderContent()}
      </Paper>
    </Box>
  );
};

export default AcceptConnectionPage;

